import { useEffect, useState, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { logUrlParamsChange } from '../utils/urlParamsLogger'

/**
 * Custom hook to manage URL parameters and ensure they persist across navigation
 * This hook is specifically designed for QR code orders where parameters must be preserved
 */
export const useUrlParams = () => {
    const location = useLocation()
    const [urlParams, setUrlParams] = useState<URLSearchParams>(new URLSearchParams())

    // Update URL parameters whenever location changes
    useEffect(() => {
        const params = new URLSearchParams(location.search)
        setUrlParams(params)

        // Log current parameters for debugging
        logUrlParamsChange('useUrlParams', params)
    }, [location.search])

    // Get specific parameter value
    const getParam = useCallback((key: string): string | null => {
        return urlParams.get(key)
    }, [urlParams])

    // Get all parameters as object
    const getAllParams = useCallback(() => {
        const params: Record<string, string> = {}
        urlParams.forEach((value, key) => {
            params[key] = value
        })
        return params
    }, [urlParams])

    // Get parameters string for navigation
    const getParamsString = useCallback((): string => {
        return urlParams.toString()
    }, [urlParams])

    // Check if required QR parameters exist
    const hasRequiredParams = useCallback((): boolean => {
        const branchId = urlParams.get('branchId')
        const typeId = urlParams.get('typeId')
        return !!(branchId && typeId)
    }, [urlParams])

    // Get order information for display
    const getOrderInfo = useCallback(() => {
        const typeId = urlParams.get('typeId') ? parseInt(urlParams.get('typeId')!) : 2
        const orderType = typeId === 3 ? 'Table' : 'Branch'
        const branchName = urlParams.get('branchName')
            ? decodeURIComponent(urlParams.get('branchName')!.replace(/\+/g, ' '))
            : 'Branch'

        let tableNumber = null
        if (typeId === 3) {
            if (urlParams.get('tableName')) {
                tableNumber = decodeURIComponent(urlParams.get('tableName')!.replace(/\+/g, ' '))
            } else if (urlParams.get('tableId')) {
                tableNumber = urlParams.get('tableId')
            }
        }

        return {
            branchName,
            orderType,
            tableNumber
        }
    }, [urlParams])

    // Get API parameters
    const getApiParams = useCallback(() => {
        const branchId = urlParams.get('branchId') ? parseInt(urlParams.get('branchId')!) : undefined
        const typeId = urlParams.get('typeId') ? parseInt(urlParams.get('typeId')!) : undefined

        return { branchId, typeId }
    }, [urlParams])

    return {
        urlParams,
        getParam,
        getAllParams,
        getParamsString,
        hasRequiredParams,
        getOrderInfo,
        getApiParams
    }
}